// @flow

import { toASCII } from 'punycode.js';

import { selectPricingInfos } from '@gandi/billing';
import { selectDomainCreateProducts, selectCartCurrency } from '@gandi/cart';
import { selectFromConfig } from '@gandi/redux-config';
import { selectCurrentId } from '@gandi/redux-sharing';
import { localeSelector as selectLocale } from '@gandi/translate';

import { selectFqdnSoldWithHosting } from 'src/WebHosting/modules/selectors';

import type { FullState } from '../../../types';
import makeQueryString from '../helpers/makeQueryString';
import { selectFilterValue } from '../selectors/filters';
import { selectPagination } from '../selectors/pagination';
import {
  selectSearchValue,
  selectRequiredAvailables,
} from '../selectors/search';
import { selectSource } from '../selectors/source';

type Params = {
  country: string,
  grid: string,
  currency: string,
  sharing_id: string,
  lang: string,
  search: string,
  tlds: Array<string>,
  phases: Array<string>,
  lock_sentence: boolean,
  page: number,
  per_page: number,
  required_availables: number,
  exclude: boolean,
  source: string,
  discount?: boolean,
  filter_list?: string,
  ...
};

/**
 * Return the AIOSuggest API parameters object.
 * Other parameters are forwarded to:
 *   - Prices API
 *   - TLD/Name Suggest API
 */
function getSuggestParameters(
  state: FullState,
  page: number = 1,
  perPage: number = 30,
): Params {
  const tlds: Array<string> = selectFilterValue(state, 'tlds')
    .split(',')
    .filter(tld => tld !== 'suggested');

  const phases = selectFilterValue(state, 'phases').split(',');
  const sales = selectFilterValue(state, 'sales');
  const filter_list = selectFilterValue(state, 'filter_list');
  const requiredAvailables = selectRequiredAvailables(state);
  const searchValue = selectSearchValue(state)
    .split(' ')
    .map(toASCII)
    .join(' ');
  const useNameSuggest = selectFilterValue(state, 'semantic') === 'semantic';

  const source = selectSource(state);
  const lang = selectLocale(state);
  const sharingID = selectCurrentId(state);
  // $FlowFixMe[incompatible-call]
  const { currency, country, grid } = selectPricingInfos(state);
  const cartCurrency = selectCartCurrency(state);

  const domainCreateProducts = selectDomainCreateProducts(state);

  const webhostingDomainsProducts = selectFqdnSoldWithHosting(state);
  const domainCreateInCart = domainCreateProducts.map(
    product => product.payload.fqdn,
  );

  const exclude = [...domainCreateInCart, ...webhostingDomainsProducts].filter(
    fqdn => (fqdn ? !searchValue.includes(fqdn) : true),
  );

  // parameters for the querystring passed to the suggest server
  const params: Params = {
    // Prices API parameters
    country,
    grid,
    currency: cartCurrency || currency,
    sharing_id: sharingID,

    // TLD/Name Suggest API parameters
    lang,
    search: searchValue,
    tlds,
    phases,
    filter_list,
    lock_sentence: !useNameSuggest,

    // Pagination
    page,
    per_page: perPage,

    // Loop en SSE events until a minimum available threshold is met.
    required_availables: requiredAvailables,

    // Exclude
    // $FlowFixMe boolean?
    exclude,

    // Trafic source
    source,
  };

  if (sales !== 'show') {
    params.discount = sales === 'show-only';
  }

  return params;
}

/**
 * Build AIOSuggest API URL with querystring parameters.
 */
const selectSuggestURL = selectFromConfig<string>('api.suggestUrl');
export const getSuggestURL = (state: FullState): string => {
  const suggestURL = selectSuggestURL(state);
  const { page, perPage } = selectPagination(state);

  const parameters = getSuggestParameters(state, page, perPage);
  const querystring = makeQueryString(parameters);

  return `${suggestURL}?${querystring}`;
};
